import { useField } from 'formik';
import {
  Radio,
  FormLabel,
  RadioGroup,
  FormControl,
  FormHelperText,
  RadioGroupProps,
  FormControlLabel,
} from '@mui/material';

import { controlError } from 'utils';
import { QuestionLabel } from 'components/question-label';

export interface RadioFieldProps extends Omit<
  RadioGroupProps,
  'name' | 'onChange'
> {
  name: string;
  label?: string;
  radioClassName?: string;
  controlErrorLabel?: boolean;
  radioGroupClassName?: string;
  // Required when mapValue stores an object instead of the option value.
  displayValue?: string | string[];
  mapValue?: (value: string) => unknown;
  options: { label: string; value: string | number }[];
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => void;
}

export const RadioField = ({
  name,
  label,
  options,
  onChange,
  mapValue,
  displayValue,
  radioClassName,
  radioGroupClassName,
  ...props
}: RadioFieldProps) => {
  const [field, meta, helpers] = useField(name);

  const errorText = controlError(meta, name, label);
  const hasError = Boolean(errorText);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    helpers.setValue(mapValue ? mapValue(value) : value, true);
    onChange?.(event, value);
  };

  return (
    <FormControl error={hasError}>
      {label && (
        <FormLabel component="div">
          <QuestionLabel value={label} />
        </FormLabel>
      )}
      <RadioGroup
        {...props}
        name={field.name}
        onChange={handleChange}
        className={radioGroupClassName}
        value={displayValue ?? field.value ?? ''}
      >
        {options.map((opt) => (
          <FormControlLabel
            key={opt.value}
            value={opt.value}
            className="items-start"
            control={<Radio className={radioClassName} />}
            label={<p className="min-h-6 align-middle">{opt.label}</p>}
          />
        ))}
      </RadioGroup>
      {hasError && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};
